import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle,
  MapPin,
  RotateCcw,
  Save,
  Shield,
  Users,
  UserX,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import {
  createEmployeeCustomerMapApi,
  updateEmployeeCustomerMapApi,
  type ICreateEmployeeCustomerMapDto,
} from '../api/client-customer-map.api';
import type {
  IAreaCustomerMap,
  IClientCustomerMap,
  IEmployeeCustomerMap,
  IEmployeeCustomerMapDetailed,
} from '../types';

import TwoColumnSelectList from './TwoColumnSelectList';

interface EmployeeMappingTabsProps {
  selectedEmployee: IEmployeeCustomerMap;
  employeeDetails: IEmployeeCustomerMapDetailed | null;
  clients: IClientCustomerMap[];
  areas: IAreaCustomerMap[];
  onMappingUpdated: () => void;
  avatarServerPath?: string;
}

export default function EmployeeMappingTabs({
  selectedEmployee,
  employeeDetails,
  clients,
  areas,
  onMappingUpdated,
  avatarServerPath,
}: EmployeeMappingTabsProps) {
  const [activeTab, setActiveTab] = useState('clients');
  const [hasChanges, setHasChanges] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [mappingData, setMappingData] = useState<ICreateEmployeeCustomerMapDto>(
    {
      employee: selectedEmployee._id,
      clients: [],
      areas: [],
      excludeClients: [],
      isFullAccess: false,
    },
  );

  const queryClient = useQueryClient();

  // Initialize mapping data when employee details change
  useEffect(() => {
    if (employeeDetails) {
      setMappingData({
        employee: selectedEmployee._id,
        clients: employeeDetails.clients || [],
        areas: employeeDetails.areas || [],
        excludeClients: employeeDetails.excludeClients || [],
        isFullAccess: employeeDetails.isFullAccess || false,
      });
      setHasChanges(false);
    }
  }, [employeeDetails, selectedEmployee._id]);

  // Create mapping mutation
  const createMappingMutation = useMutation({
    mutationFn: createEmployeeCustomerMapApi,
    onSuccess: () => {
      toast.success('تم إنشاء توزيع الموظف بنجاح');
      queryClient.invalidateQueries({
        queryKey: ['employee-customer-map-details', selectedEmployee._id],
      });
      onMappingUpdated();
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error('حدث خطأ أثناء إنشاء توزيع الموظف');
      console.error('Create mapping error:', error);
    },
  });

  // Update mapping mutation
  const updateMappingMutation = useMutation({
    mutationFn: ({
      employeeId,
      data,
    }: {
      employeeId: string;
      data: ICreateEmployeeCustomerMapDto;
    }) => updateEmployeeCustomerMapApi(employeeId, data),
    onSuccess: () => {
      toast.success('تم تحديث توزيع الموظف بنجاح');
      queryClient.invalidateQueries({
        queryKey: ['employee-customer-map-details', selectedEmployee._id],
      });
      onMappingUpdated();
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error('حدث خطأ أثناء تحديث توزيع الموظف');
      console.error('Update mapping error:', error);
    },
  });

  const handleSave = () => {
    if (employeeDetails?.hasMapping) {
      updateMappingMutation.mutate({
        employeeId: selectedEmployee._id,
        data: mappingData,
      });
    } else {
      createMappingMutation.mutate(mappingData);
    }
    setSaveDialogOpen(false);
  };

  const handleDataChange = (
    newData: Partial<ICreateEmployeeCustomerMapDto>,
  ) => {
    const updatedData = { ...mappingData, ...newData };
    setMappingData(updatedData);

    // Check if there are changes compared to original data
    const hasDataChanges =
      JSON.stringify(updatedData.clients?.sort()) !==
        JSON.stringify(employeeDetails?.clients?.sort()) ||
      JSON.stringify(updatedData.areas?.sort()) !==
        JSON.stringify(employeeDetails?.areas?.sort()) ||
      JSON.stringify(updatedData.excludeClients?.sort()) !==
        JSON.stringify(employeeDetails?.excludeClients?.sort()) ||
      updatedData.isFullAccess !== employeeDetails?.isFullAccess;

    setHasChanges(hasDataChanges);
  };

  const handleClientsChange = (selectedIds: number[]) => {
    handleDataChange({ clients: selectedIds });
  };

  const handleAreasChange = (selectedIds: number[]) => {
    handleDataChange({ areas: selectedIds });
  };

  const handleExcludeClientsChange = (selectedIds: number[]) => {
    handleDataChange({ excludeClients: selectedIds });
  };

  const handleFullAccessToggle = (checked: boolean) => {
    handleDataChange({ isFullAccess: checked });
  };

  const handleReset = () => {
    if (employeeDetails) {
      setMappingData({
        employee: selectedEmployee._id,
        clients: employeeDetails.clients || [],
        areas: employeeDetails.areas || [],
        excludeClients: employeeDetails.excludeClients || [],
        isFullAccess: employeeDetails.isFullAccess || false,
      });
      setHasChanges(false);
      setResetDialogOpen(false);
      toast.success('تم إعادة تعيين التغييرات بنجاح');
    }
  };

  const isLoading =
    createMappingMutation.isPending || updateMappingMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
          إدارة التوزيع
        </h3>
        {hasChanges && (
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            {/* Reset Button with Confirmation */}
            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="default"
                  disabled={isLoading}
                  leftIcon={<RotateCcw className="h-4 w-4" />}
                  className="border-orange-200 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-300 dark:hover:bg-orange-900/20"
                >
                  إعادة تعيين
                </Button>
              </DialogTrigger>
              <DialogContent size="md" className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 text-xl font-bold">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-red-600 shadow-lg">
                      <AlertTriangle className="h-5 w-5 text-white" />
                    </div>
                    <span>تأكيد إعادة التعيين</span>
                  </DialogTitle>
                  <DialogDescription className="text-base leading-relaxed">
                    سيتم فقدان جميع التعديلات التي قمت بها
                  </DialogDescription>
                </DialogHeader>

                <DialogBody>
                  <div className="space-y-4">
                    {/* Warning Card */}
                    <div className="rounded-xl border border-orange-200/60 bg-gradient-to-br from-orange-50/80 to-red-50/80 p-6 shadow-sm backdrop-blur-sm dark:border-orange-700/60 dark:from-orange-900/20 dark:to-red-900/20">
                      <div className="flex items-start gap-4">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/40">
                          <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="flex-1">
                          <h3 className="mb-2 font-semibold text-orange-900 dark:text-orange-100">
                            تحذير مهم
                          </h3>
                          <p className="text-sm leading-relaxed text-orange-800 dark:text-orange-200">
                            عند إعادة التعيين، سيتم فقدان جميع التغييرات
                            التالية:
                          </p>
                          <ul className="mt-3 space-y-2 text-sm text-orange-700 dark:text-orange-300">
                            <li className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-orange-500"></div>
                              إعدادات العملاء المخصصين
                            </li>
                            <li className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-orange-500"></div>
                              إعدادات المناطق المخصصة
                            </li>
                            <li className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-orange-500"></div>
                              قائمة العملاء المستبعدين
                            </li>
                            <li className="flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-orange-500"></div>
                              إعدادات الصلاحيات
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Irreversible Warning */}
                    <div className="rounded-lg border border-red-200/60 bg-gradient-to-r from-red-50/80 to-pink-50/80 p-4 dark:border-red-700/60 dark:from-red-900/20 dark:to-pink-900/20">
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
                          <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </div>
                        <div className="text-sm">
                          <p className="font-medium text-red-800 dark:text-red-200">
                            إجراء لا يمكن التراجع عنه
                          </p>
                          <p className="text-red-700 dark:text-red-300">
                            لن يمكن استرداد هذه التغييرات بعد إعادة التعيين
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogBody>

                <DialogFooter className="flex gap-3">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="default"
                      onClick={() => setResetDialogOpen(false)}
                      className="min-w-[100px]"
                    >
                      إلغاء
                    </Button>
                    <Button
                      variant="destructive"
                      size="default"
                      onClick={handleReset}
                      leftIcon={<RotateCcw className="h-4 w-4" />}
                      className="min-w-[120px]"
                    >
                      إعادة تعيين
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Save Button with Confirmation */}
            <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="default"
                  size="lg"
                  loading={isLoading}
                  leftIcon={<Save className="h-4 w-4" />}
                  className="min-w-[140px] shadow-lg hover:shadow-xl"
                >
                  حفظ التوزيع
                </Button>
              </DialogTrigger>
              <DialogContent size="lg" className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 text-xl font-bold">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
                      <Save className="h-5 w-5 text-white" />
                    </div>
                    <span>تأكيد حفظ التغييرات</span>
                  </DialogTitle>
                  <DialogDescription className="text-base leading-relaxed">
                    يرجى مراجعة التغييرات التالية قبل الحفظ
                  </DialogDescription>
                </DialogHeader>

                <DialogBody>
                  <div className="space-y-4">
                    {/* Summary Card */}
                    <div className="rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50/80 to-white/80 p-6 shadow-sm backdrop-blur-sm dark:border-slate-700/60 dark:from-slate-800/80 dark:to-slate-900/80">
                      <div className="mb-4 flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                          ملخص التغييرات
                        </h3>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between rounded-lg bg-white/60 p-3 shadow-sm dark:bg-slate-800/60">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-blue-500" />
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              العملاء المخصصين
                            </span>
                          </div>
                          <Badge variant="soft" className="font-semibold">
                            {mappingData.clients?.length || 0}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between rounded-lg bg-white/60 p-3 shadow-sm dark:bg-slate-800/60">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-green-500" />
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              المناطق المخصصة
                            </span>
                          </div>
                          <Badge variant="soft" className="font-semibold">
                            {mappingData.areas?.length || 0}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between rounded-lg bg-white/60 p-3 shadow-sm dark:bg-slate-800/60">
                          <div className="flex items-center gap-2">
                            <UserX className="h-4 w-4 text-red-500" />
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              العملاء المستبعدين
                            </span>
                          </div>
                          <Badge variant="soft" className="font-semibold">
                            {mappingData.excludeClients?.length || 0}
                          </Badge>
                        </div>

                        <div className="flex items-center justify-between rounded-lg bg-white/60 p-3 shadow-sm dark:bg-slate-800/60">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-purple-500" />
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              صلاحيات كاملة
                            </span>
                          </div>
                          <Badge
                            variant={
                              mappingData.isFullAccess ? 'soft' : 'outline'
                            }
                            className="font-semibold"
                          >
                            {mappingData.isFullAccess ? 'نعم' : 'لا'}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {/* Warning Message */}
                    <div className="rounded-lg border border-amber-200/60 bg-gradient-to-r from-amber-50/80 to-yellow-50/80 p-4 dark:border-amber-700/60 dark:from-amber-900/20 dark:to-yellow-900/20">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-800 dark:text-amber-200">
                            تنبيه
                          </p>
                          <p className="text-amber-700 dark:text-amber-300">
                            سيتم حفظ هذه التغييرات بشكل دائم ولا يمكن التراجع
                            عنها
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </DialogBody>

                <DialogFooter className="flex items-center gap-3">
                  <div className="flex-1">
                    <Button
                      variant="outline"
                      size="default"
                      onClick={() => setSaveDialogOpen(false)}
                      className="min-w-[100px]"
                    >
                      إلغاء
                    </Button>
                    <Button
                      variant="default"
                      size="default"
                      onClick={handleSave}
                      leftIcon={<Save className="h-4 w-4" />}
                      className="min-w-[120px] bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                    >
                      تأكيد الحفظ
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid h-14 w-full grid-cols-4 rounded-xl border border-slate-200 bg-slate-50 p-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
          <TabsTrigger
            value="clients"
            className="flex items-center space-x-2 rounded-lg border-0 bg-transparent px-4 py-3 text-sm font-medium text-slate-600 transition-all duration-300 hover:bg-white hover:text-blue-600 hover:shadow-md data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-blue-400 dark:data-[state=active]:from-blue-600 dark:data-[state=active]:to-indigo-700 rtl:space-x-reverse"
          >
            <Users className="h-4 w-4" />
            <span>العملاء</span>
            {mappingData.clients && mappingData.clients.length > 0 && (
              <Badge
                variant="soft"
                className="ml-1 bg-white/20 text-white dark:bg-slate-700/50"
              >
                {mappingData.clients.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="areas"
            className="flex items-center space-x-2 rounded-lg border-0 bg-transparent px-4 py-3 text-sm font-medium text-slate-600 transition-all duration-300 hover:bg-white hover:text-green-600 hover:shadow-md data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-lg dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-green-400 dark:data-[state=active]:from-green-600 dark:data-[state=active]:to-emerald-700 rtl:space-x-reverse"
          >
            <MapPin className="h-4 w-4" />
            <span>المناطق</span>
            {mappingData.areas && mappingData.areas.length > 0 && (
              <Badge
                variant="soft"
                className="ml-1 bg-white/20 text-white dark:bg-slate-700/50"
              >
                {mappingData.areas.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="excluded"
            className="flex items-center space-x-2 rounded-lg border-0 bg-transparent px-4 py-3 text-sm font-medium text-slate-600 transition-all duration-300 hover:bg-white hover:text-red-600 hover:shadow-md data-[state=active]:bg-gradient-to-r data-[state=active]:from-red-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-lg dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-red-400 dark:data-[state=active]:from-red-600 dark:data-[state=active]:to-pink-700 rtl:space-x-reverse"
          >
            <UserX className="h-4 w-4" />
            <span>المستبعدين</span>
            {mappingData.excludeClients &&
              mappingData.excludeClients.length > 0 && (
                <Badge
                  variant="soft"
                  className="ml-1 bg-white/20 text-white dark:bg-slate-700/50"
                >
                  {mappingData.excludeClients.length}
                </Badge>
              )}
          </TabsTrigger>
          <TabsTrigger
            value="settings"
            className="flex items-center space-x-2 rounded-lg border-0 bg-transparent px-4 py-3 text-sm font-medium text-slate-600 transition-all duration-300 hover:bg-white hover:text-purple-600 hover:shadow-md data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-lg dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-purple-400 dark:data-[state=active]:from-purple-600 dark:data-[state=active]:to-violet-700 rtl:space-x-reverse"
          >
            <CheckCircle className="h-4 w-4" />
            <span>الإعدادات</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="space-y-4">
          <TwoColumnSelectList
            availableItems={clients.map((client) => ({
              id: client.ID,
              name: client.ClientName,
              code: client.ID.toString(),
              regionId: client.RegionID,
            }))}
            selectedItems={mappingData.clients || []}
            onSelectionChange={handleClientsChange}
            title="اختيار العملاء"
            searchPlaceholder="البحث عن العملاء..."
            emptyMessage="لا توجد عملاء متاحة"
            selectedEmployee={selectedEmployee}
            avatarServerPath={avatarServerPath}
            areas={areas}
          />
        </TabsContent>

        <TabsContent value="areas" className="space-y-4">
          <TwoColumnSelectList
            availableItems={areas.map((area) => ({
              id: area.ID,
              name: area.Name,
              code: `${area.totalClients} عميل`,
            }))}
            selectedItems={mappingData.areas || []}
            onSelectionChange={handleAreasChange}
            title="اختيار المناطق"
            searchPlaceholder="البحث عن المناطق..."
            emptyMessage="لا توجد مناطق متاحة"
            selectedEmployee={selectedEmployee}
            avatarServerPath={avatarServerPath}
            areas={areas}
          />
        </TabsContent>

        <TabsContent value="excluded" className="space-y-4">
          <TwoColumnSelectList
            availableItems={clients.map((client) => ({
              id: client.ID,
              name: client.ClientName,
              code: client.ID.toString(),
              regionId: client.RegionID,
            }))}
            selectedItems={mappingData.excludeClients || []}
            onSelectionChange={handleExcludeClientsChange}
            title="العملاء المستبعدون"
            searchPlaceholder="البحث عن العملاء المستبعدين..."
            emptyMessage="لا توجد عملاء متاحة"
            selectedEmployee={selectedEmployee}
            avatarServerPath={avatarServerPath}
            areas={areas}
          />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
            <h4 className="mb-4 font-semibold text-slate-900 dark:text-white">
              إعدادات الصلاحيات
            </h4>
            <div className="space-y-4">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <Checkbox
                  checked={mappingData.isFullAccess || false}
                  onCheckedChange={(checked) =>
                    handleFullAccessToggle(checked as boolean)
                  }
                />
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    صلاحيات كاملة
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    السماح بالوصول إلى جميع العملاء والمناطق دون قيود
                  </p>
                </div>
              </div>

              {mappingData.isFullAccess && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <CheckCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      تنبيه: عند تفعيل الصلاحيات الكاملة، سيتم تجاهل اختيارات
                      العملاء والمناطق المحددة أعلاه.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
